import axios, { type AxiosInstance } from "axios";

import { backendUrl } from "../../../api/backend/axios";
import { readSseStream } from "../../../utils/sse";
import { clearToken, getToken } from "../utils/sessionToken";
import { getAttribution } from "../utils/attribution";

const client: AxiosInstance = axios.create({
  baseURL: `${backendUrl}/public-api/jan`,
  timeout: 60_000,
});

client.interceptors.request.use((cfg) => {
  // Non-clobbering: only the default-variant token is injected here. Variant-aware
  // callers (submitMessage/submitEmail) set X-Public-Session explicitly with their
  // own variant-scoped token, and we must not override it.
  const t = getToken();
  // Inject only when the header is genuinely absent. submitEmail sets an
  // explicit `""` for a variant with no token; that empty string must survive
  // (it deliberately withholds a fallback) rather than being clobbered here.
  if (t && cfg.headers["X-Public-Session"] === undefined) {
    cfg.headers["X-Public-Session"] = t;
  }
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e?.response?.status === 401) clearToken();
    return Promise.reject(e);
  }
);

export interface StartResponse {
  token: string;
  chat_id: string;
}

export interface PublicProfession {
  id: string;
  label: string;
}

/**
 * Profession list for the email gate, proxied + cached by the backend from
 * NovoPsych's public `/statics` endpoint. Public — no session needed.
 */
export const getProfessions = async (): Promise<PublicProfession[]> => {
  const res = await client.get<{ professions: PublicProfession[] }>(
    "/professions"
  );
  return res.data.professions;
};

export interface PublicChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

/**
 * Outcome of a /message POST. The backend either streams the assistant reply
 * (SSE) or short-circuits with a JSON email gate — those are the only two
 * outcomes, so model them as a discriminated union rather than an optional
 * flag (which made `{}` a valid-but-meaningless value of the type).
 */
export type SubmitMessageResult =
  | { kind: "streamed" }
  | { kind: "email_required" };

export interface StartSessionArgs {
  // Cloudflare Turnstile token captured by the widget. Required when the
  // backend has TURNSTILE_SECRET_KEY set; safe to omit otherwise (backend
  // bypasses verification when the secret is unset).
  turnstileToken?: string;
  /** Variant slug to bind the session to. Defaults to "default". */
  variant?: string;
}

export const startSession = async (
  args: StartSessionArgs = {}
): Promise<StartResponse> => {
  const body: Record<string, string> = { variant: args.variant ?? "default" };
  if (args.turnstileToken) body.turnstileToken = args.turnstileToken;
  // Attach marketing attribution captured at page load. getAttribution()
  // returns {} when none was captured, so the body is unchanged for organic
  // traffic. Attribution is best-effort: if the backend rejects the body
  // (e.g. an older deploy whose schema doesn't know these keys → 400, or an
  // oversize payload → 413), retry once WITHOUT it rather than blocking the
  // chat — losing one row of marketing data beats losing the visitor.
  //
  // The retry re-sends the same single-use turnstileToken, which is only safe
  // while the token hasn't been consumed by siteverify. Both rejections happen
  // pre-verification (413 from body-parser, 400 from the schema parse), but
  // guard the 400 on the schema parse's `bad_body` code so a future
  // post-verification 400 can't trigger a retry with a spent token. 413 has no
  // error code to check (body-parser rejects before the handler), but it is
  // inherently pre-verification, so status alone is safe there.
  const attribution = getAttribution();
  try {
    const res = await client.post<StartResponse>("/session/start", {
      ...body,
      ...attribution,
    });
    return res.data;
  } catch (e) {
    const err = e as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = err?.response?.status;
    const code = err?.response?.data?.error;
    if (
      Object.keys(attribution).length > 0 &&
      ((status === 400 && code === "bad_body") || status === 413)
    ) {
      const res = await client.post<StartResponse>("/session/start", body);
      return res.data;
    }
    throw e;
  }
};

export const submitMessage = async (
  chatId: string,
  message: string,
  onChunk: (s: string) => void,
  variant = "default"
): Promise<SubmitMessageResult> => {
  const token = getToken(variant);
  const res = await fetch(
    `${backendUrl}/public-api/jan/chat/${chatId}/message`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "X-Public-Session": token } : {}),
      },
      body: JSON.stringify({ message }),
    }
  );

  if (!res.ok) {
    if (res.status === 401) clearToken(variant);
    throw new Error(`http_${res.status}`);
  }

  // Email gate: backend short-circuits with `{ requireEmail: true }` JSON
  // instead of streaming once the free-turn limit is hit.
  if ((res.headers.get("content-type") ?? "").includes("application/json")) {
    return { kind: "email_required" };
  }

  await readSseStream(res, (ev) => {
    if (ev.data === "[DONE]") return;
    onChunk(ev.data);
  });

  return { kind: "streamed" };
};

export const submitEmail = async (
  email: string,
  professionId: string,
  variant = "default"
): Promise<void> => {
  const token = getToken(variant);
  try {
    await client.post(
      "/session/email",
      { email, profession_id: professionId },
      { headers: { "X-Public-Session": token ?? "" } }
    );
  } catch (e) {
    // The shared response interceptor only clears the default-variant token on
    // 401; clear this variant's token too so a stale session is re-minted.
    if (axios.isAxiosError(e) && e.response?.status === 401)
      clearToken(variant);
    throw e;
  }
};

export interface PublicVariantConfig {
  welcome: string;
  starters: string[];
}

/**
 * Fetch the public, session-less config (welcome + starter chips) for a
 * variant. Used by the embed to render its initial state before a session
 * exists. Backed by GET /public-api/jan/variants/:slug/config (no token).
 */
export const getPublicVariantConfig = async (
  slug: string
): Promise<PublicVariantConfig> => {
  const res = await client.get<PublicVariantConfig>(`/variants/${slug}/config`);
  return res.data;
};

/**
 * Request a server-rendered PDF for the given markdown. The backend pipes
 * the markdown through Puppeteer; PDFs of long messages with multiple
 * Mermaid diagrams routinely take 5-15s, so we override the default
 * 60s timeout with a more generous 90s ceiling.
 */
export const requestPdf = async (
  markdown: string,
  filename: string
): Promise<Blob> => {
  const res = await client.post<Blob>(
    "/generate-pdf",
    { markdown, filename },
    { responseType: "blob", timeout: 90_000 }
  );
  return res.data;
};
